import type {ReactElement} from 'react';
import React from 'react';
import {Text, TextSize} from '@Components/text';
import {Thumbnail} from '@Components/thumbnail';
import type {ThumbnailData} from '@Components/thumbnail/thumbnail.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import type {BaseControlledListItem} from '@Components/controlled-list/controlled-list.types';
import {noop} from '@Utils/general.util';
import {ActivityIndicatorSize, ActivityIndicatorType} from '@Components/activity-indicator/activity-indicator.types';
import {ActivityIndicator} from '@Components/activity-indicator/activity-indicator';
import styles from './controlled-list-item-type-2.module.scss';
import type {Pill} from '@Components/pill';

export interface ControlledListItemType2Props extends BaseControlledListItem {
  thumbnail?: ThumbnailData;
  hasPulsatingDot?: boolean;
  hasTextPulsatingDot?: boolean;
  activityIndicatorType?: ActivityIndicatorType;
  subText: string | ReactElement;
  isDisabled?: boolean;
  disableHoverTransitions?: boolean;
  isTextBold?: boolean;
  textClasses?: string;
  thumbnailContainerClasses?: string;
  pill?: ReactElement<typeof Pill>;
  textSize?: TextSize;
  onClick?(id?: string, event?: React.MouseEvent<HTMLElement>): void;
  renderAsDiv?: boolean;
}

export function ControlledListItemType2({
  className = '',
  id = '',
  isSelected = false,
  isDisabled = false,
  hasPulsatingDot = false,
  hasTextPulsatingDot = false,
  onClick = noop,
  disableHoverTransitions = false,
  isTextBold = true,
  textClasses = '',
  textSize = TextSize.XSMALL,
  ...props
}: ControlledListItemType2Props): ReactElement {
  const activityIndicatorType = hasPulsatingDot ? ActivityIndicatorType.NEW_PULSE : props.activityIndicatorType;
  const activityIndicatorTextType = hasTextPulsatingDot ? ActivityIndicatorType.NEW_PULSE : props.activityIndicatorType;
  const onClickHandler = (event: React.MouseEvent<HTMLElement>): void => {
    if (onClick && !isDisabled) {
      onClick(id, event);
    }
  };

  const getThumbnailClasses = (): string => {
    if (!props.thumbnail) {
      return '';
    }

    if (props.thumbnail.type === ThumbnailType.ICON_DEFAULT) {
      return `${styles.iconDefault} flex-center radius-4 hover-transition-all`;
    }

    return `${props.thumbnail.type === ThumbnailType.CUSTOM ? props.thumbnail.className : ''} ${props.thumbnail.type !== ThumbnailType.ICON ? styles.image : ''}`;
  };

  const displayThumbnail = (): ReactElement | null => {
    if (props.thumbnail) {
      return (
        <div className={`flexbox ${styles.thumbnailContainer} ${props.thumbnailContainerClasses}`}>
          {activityIndicatorType ? <ActivityIndicator size={ActivityIndicatorSize.SIZE_16} type={activityIndicatorType} className={styles.pulsePosition} /> : null}
          <Thumbnail className={getThumbnailClasses()} data={props.thumbnail} />
        </div>
      );
    }
    return null;
  };

  const getSubText = (): ReactElement | string => {
    if (typeof props.subText === 'string') {
      return <Text val={props.subText} className={isSelected ? 'content-primary' : 'content-sub-text'} size={TextSize.XXSMALL} />;
    }
    return props.subText ?? null;
  };

  const getActivityIndicatorIfNeeded = (): ReactElement<any> | null => {
    if (activityIndicatorTextType) {
      return <ActivityIndicator size={ActivityIndicatorSize.SIZE_16} type={activityIndicatorTextType} />;
    }
    return null;
  };

  const display = (): ReactElement => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const RootTag = props.renderAsDiv ? 'div' : 'li';

    return (
      <RootTag
        key={id}
        data-testid={id ? id.toUpperCase() : undefined}
        onClick={(event) => {
          onClickHandler(event);
        }}
        className={`
          ${className} spacing-p-1 flexbox flex-items-center flex-justify-start 
          ${isSelected ? styles.selected : ''} 
          ${isDisabled ? styles.disabledList : ''}
          ${styles.listItemContainer} 
          ${!disableHoverTransitions ? 'hover-transition-all' : ''} 
        `}
      >
        {displayThumbnail()}
        <div className={`${styles.textBox} flex-v-row`}>
          <div className="flexbox flex-align-items-center">
            <div className={styles.containerWithIndicator}>
              <Text val={props.text} className={`${textClasses} ${isDisabled ? styles.disableText : ''} ${isSelected ? 'content-primary' : 'content-body'}`} size={textSize} bold={isTextBold} />
              {getActivityIndicatorIfNeeded()}
            </div>
            <div>{props.pill}</div>
          </div>
          {getSubText()}
        </div>
      </RootTag>
    );
  };

  return <>{display()}</>;
}
